import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Divider, IconButton, Skeleton, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useQuery } from '@tanstack/react-query';
import currency from 'currency.js';
import { parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { IAccount, IBill } from 'common/src/types/interfaces';

import { getAccountStats } from 'portal/src/api/AccountsService';
import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import FormController from 'portal/src/components/common/forms/FormController';

import { useBillFormContext } from './BillFormContext';

const BillFormAccountInfo = ({
  account,
  billUsedInAllocation,
}: {
  account: Partial<IAccount>;
  billUsedInAllocation: boolean;
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const isReadOnly = hasReadOnlyAccess();
  const {
    accountId,
    accounts: { data: accounts },
    bill,
    headerForm: { setValue: setHeaderValue },
    previousBill: {
      data: previousBill,
      isFetching: previousBillIsFetching,
      isError: isErrorPreviousBill,
    },
    setAccountId,
  } = useBillFormContext();

  const {
    data: accountStats,
    isFetching: isFetchingAccountStats,
    isError: isErrorAccountStats,
  } = useQuery({
    queryKey: ['accountStats'],
    queryFn: async () => await getAccountStats(account.id),
    enabled: !!account.id,
    initialData: undefined,
  });

  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);

  const { control, setValue, getValues } = useForm({
    defaultValues: {
      accountId: account.id || accountId,
    },
  });

  useEffect(() => {
    setValue('accountId', account.id || accountId);
  }, [account.id, accountId, setValue]);

  useEffect(() => {
    if (accountId && previousBill && !bill.id) {
      setHeaderValue(
        'startDate',
        previousBill.endDate ? parseISO(previousBill.endDate) : null,
        { shouldDirty: true },
      );
    }
  }, [accountId, bill.id, previousBill, setHeaderValue]);

  const accountOptions = useMemo(() => {
    const options = accounts.map((a) => ({
      label: a.number,
      value: a.id,
      disabled: !a.isActive,
    }));

    const currentAccountId = account.id || accountId;
    const hasCurrentAccount = options.some(
      (option) => option.value === currentAccountId,
    );

    if (currentAccountId && !hasCurrentAccount) {
      options.unshift({
        label: account.number || currentAccountId,
        value: currentAccountId,
        disabled: false,
      });
    }

    return options;
  }, [accounts, account.id, account.number, accountId]);

  const selectedAccount =
    accounts.find((a) => a.id === (account.id || accountId)) ?? account;

  const createPreviousBillDateRange = (previousBill: IBill) => {
    if (previousBill?.startDate && previousBill?.endDate) {
      const startDate = parseISO(previousBill.startDate).toLocaleDateString(
        'en-US',
      );
      const endDate = parseISO(previousBill.endDate).toLocaleDateString(
        'en-US',
      );
      return `${startDate} - ${endDate}`;
    }
    return '';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        px: 1.8,
        py: 0.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: grey[400],
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        width="50%"
      >
        {showEditAccount ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              backgroundColor: grey[100],
              borderRadius: 1,
              padding: 0.5,
              width: '80%',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <FormController
                control={control}
                fieldConf={{
                  clearIcon: null,
                  label: 'Account',
                  name: 'accountId',
                  options: accountOptions,
                  required: true,
                  type: 'autocomplete',
                  size: 'small',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setValue('accountId', account.id);
                  setShowEditAccount(false);
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  const selectedAccountId = getValues('accountId');

                  if (selectedAccountId && account.id !== selectedAccountId) {
                    setAccountId(selectedAccountId);
                    setShowAccountDialog(true);
                  }
                  setShowEditAccount(false);
                }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body1"
              fontWeight={500}
              fontSize={18}
              sx={{ textDecoration: 'underline' }}
            >
              Account: {selectedAccount.number || '-'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowEditAccount(true)}
              disabled={isReadOnly || billUsedInAllocation}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={0.5} width="100%">
          <Typography variant="subtitle1" fontWeight={500}>
            Utility:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              lineHeight: 1.5,
            }}
          >
            {selectedAccount.utility?.name || '-'}
          </Typography>
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 1 }} />
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={0.5}
        width="50%"
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            Previous Bill
          </Typography>
          <Typography variant="body2" ml={1}>
            {previousBillIsFetching ? (
              <Skeleton width={300} />
            ) : previousBill?.amount &&
              previousBill?.startDate &&
              previousBill?.endDate ? (
              `Dates: ${createPreviousBillDateRange(previousBill)} | Amount: ${currency(previousBill.amount).format()}`
            ) : isErrorPreviousBill ? (
              'There was an issue fetching the previous bill for this account'
            ) : (
              'Insufficient account history'
            )}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={500}>
            Last 12 Months
          </Typography>
          <Typography variant="body2" ml={1}>
            {isFetchingAccountStats ? (
              <Skeleton width="100%" sx={{ maxWidth: 350 }} />
            ) : accountStats?.numberOfBillsLast12Months ? (
              `Bill Count: ${accountStats.numberOfBillsLast12Months}` +
              ` | Total Amount: ${currency(accountStats.totalAmountLast12Months).format()}` +
              ` | Average Amount: ${currency(accountStats.avgAmountLast12Months).format()}`
            ) : isErrorAccountStats ? (
              'There was an issue fetching the account stats for this account'
            ) : (
              'Insufficient account history'
            )}
          </Typography>
        </Box>
      </Box>
      <ConfirmDialog
        open={showAccountDialog}
        onClose={() => setShowAccountDialog(false)}
        message="Reminder: be sure to update line items with meters associated with the new account number."
        primaryButtonLabel="Accept"
        onPrimaryButtonClicked={() => setShowAccountDialog(false)}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
      />
    </Box>
  );
};

export default BillFormAccountInfo;
